import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, ArrowRight, FileText, Zap, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function RenewablesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-1203">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-1204">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-1205" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-1206">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-1207">
            Intelligent Asset Management <br className="hidden md:block" data-id="element-1208" />
            <span className="text-brand-orange" data-id="element-1209">Without the Downtime.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-1210">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-1211">
              Stop letting reactive maintenance, complex fault triage, and slow
              dispatching reduce your energy yield. We deploy autonomous AI
              agents that handle predictive maintenance, fault analysis, and
              technician routing.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-1212">
              If your assets are offline waiting for a diagnosis, you're losing
              revenue.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-1213">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-1214">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-1215" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-1216">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1217">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1218">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-1219">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-1220">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-1221">
                You're Not Just Generating Power.
                <br data-id="element-1222" />
                <span className="text-gray-500" data-id="element-1223">You're Managing Uptime.</span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-1224">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-1225">
                Most renewable portfolios aren't limited by capacity — they're
                capped by operational noise. SCADA alerts flooding inboxes, O&M
                teams dispatched reactively, and compliance reports assembled
                manually from fragmented data sources.
              </motion.p>

              <div className="space-y-8" data-id="element-1226">
                {[{
                label: 'The Response Gap',
                desc: 'A turbine fault triggers an alert at 2 AM — but nobody acts until the morning shift reviews the SCADA dashboard, losing hours of generation.'
              }, {
                label: 'The O&M Trap',
                desc: 'Technician dispatch is manual, parts availability is unchecked, and the same faults recur because root cause analysis never happens systematically.'
              }, {
                label: 'The Compliance Bottleneck',
                desc: 'Grid operator reporting, environmental compliance, and investor updates consume your operations team — pulling them away from performance optimization.'
              }].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1227">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-1228">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-1229" />
                    </div>
                    <div data-id="element-1230">
                      <p className="text-lg text-white font-medium mb-1" data-id="element-1231">
                        {point.label}
                      </p>
                      <p className="text-gray-400 font-light leading-relaxed" data-id="element-1232">
                        {point.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-1233">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1234">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-1235">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1236">
              Purpose-Built Agents for Renewable Assets.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-1237">
              We don't sell AI tools. We build autonomous digital operators that
              understand renewable technology, access your SCADA systems, and
              execute real triage without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-1238">
            {[{
            icon: <Zap className="w-6 h-6 text-brand-orange" data-id="element-1239" />,
            title: 'Automated Fault Triage',
            desc: 'Agents instantly analyse SCADA alarms, cross-reference historical data, and identify the root cause before dispatching a technician.'
          }, {
            icon: <Clock className="w-6 h-6 text-brand-orange" data-id="element-1240" />,
            title: 'Predictive Maintenance',
            desc: 'Monitors asset performance trends to predict failures and automatically schedule maintenance during low-yield periods.'
          }, {
            icon: <FileText className="w-6 h-6 text-brand-orange" data-id="element-1241" />,
            title: 'Compliance Reporting',
            desc: 'Automatically generates grid compliance reports, downtime logs, and performance summaries for stakeholders and regulators.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-1242">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-1243">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-1244">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1245">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1246">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1247">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-1248">
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-1249" />

            {/* Left: Traditional Model */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-1250">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-1251">
                Traditional Model
              </h3>
              <div className="space-y-10" data-id="element-1252">
                {['SCADA fires 200 alerts overnight — operations team spends the morning triaging which ones actually matter', 'Technician dispatched to site without the right parts — returns next day, turbine still offline', 'Monthly grid compliance report takes 3 days to compile from 5 different data sources'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-1253">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-1254" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-1255">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: Aijentik Model */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-1256">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-1257">
                Aijentik Model
              </h3>
              <div className="space-y-10" data-id="element-1258">
                {['Intelligent alert filtering: 200 SCADA events reduced to 3 actionable items — auto-dispatched before sunrise', 'Technician arrives with correct parts, fault history, and resolution playbook — first-time fix, turbine back online by noon', 'Compliance report generated automatically from live asset data — submitted on schedule, audit-ready always'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-1259">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-1260" />
                    <p className="text-xl text-white font-medium" data-id="element-1261">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. USE CASES SECTION */}
      <section className="py-32" data-id="element-1262">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1263">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-1264">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-1265" />
            <span className="text-gray-500" data-id="element-1266">in Your Operations</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-1267">
            {[{
            title: 'Zero-Touch Alarm Resolution',
            desc: 'Common faults are automatically diagnosed and reset remotely without requiring a technician visit.'
          }, {
            title: 'Smart Dispatching',
            desc: 'When a visit is required, the agent ensures the technician has the correct parts, manuals, and safety protocols before they leave.'
          }, {
            title: 'Automated Yield Optimisation',
            desc: 'Continuously adjusts asset parameters based on weather forecasts and grid demands to maximise revenue.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-1268">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-1269">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1270">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1271">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1272">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-1273">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1274">
              No Fluff. Just Execution.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-1275">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-1276" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your fault triage workflows, compliance requirements, and identify the manual touchpoints slowing your response times.'
          }, {
            step: '02',
            title: 'Build',
            desc: 'We train custom agents on your specific asset types, SCADA systems, and operational protocols.'
          }, {
            step: '03',
            title: 'Integration',
            desc: 'We connect agents directly to your monitoring software and communication channels for seamless orchestration.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-1277">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-1278">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-1279">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-1280">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-1281">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-1282" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-1283" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-1284">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-1285">
            Stop Managing Downtime. <br data-id="element-1286" />
            <span className="text-brand-orange" data-id="element-1287">Start Scaling Yield.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-1288">
            Let's look at your asset operations and identify automation
            opportunities that increase your uptime.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-1289">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-1290">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1291">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}